import React, { useRef, useState } from 'react';
import 'temporal-polyfill/global'; // Apparently an import is needed for polyfill to work at runtime even if its not used directly.
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/react/interaction';
import scrollGridPlugin from '@fullcalendar/react-scheduler/scrollgrid';
import timeGridPlugin from '@fullcalendar/react/timegrid';
import themePlugin from '@fullcalendar/react/themes/monarch';
import {
  OpDayHeader,
  OpEventDialog,
  OpEventTooltip,
  OpSchedulerContextMenu,
  OpSchedulerToolbar,
  OpSchedulerSpinner,
  OpSchedulerSettingsPanel,
} from './templates';
import {
  useCustomContextMenu,
  useEventDidMount,
  useEventTooltip,
  useSchedulerConfig,
} from './hooks';
import { dateUtils, toSchedulerEvent } from './utils';

import '@fullcalendar/react/skeleton.css';
import '@fullcalendar/react/themes/monarch/theme.css';
import '@fullcalendar/react/themes/monarch/palettes/green.css';
import './opSchedulerNext.css';

// FIXME: Fix reporting svg icon - change from black to align with branding color

/* eslint-disable import/prefer-default-export */

/** @typedef {import('./OpSchedulerNext.types').SchedulerProps} SchedulerProps */

/**
 * @param {SchedulerProps} props
 */
export function OpSchedulerNext({
  eventContent,
  onDateClick,
  onDateRightClick,
  onDateDoubleClick,
  onEventClick,
  onEventRightClick,
  onEventDoubleClick,
  onEventDelete,
  onEventDrop,
  onEventCreate,
  onEventResize,
  onEventUpdate,
  onSelect,
  ...config
}) {
  const {
    schedulerConfig,
    permissions,
    contextMenuConfig,
    eventEditorConfig,
    settingsPanelConfig,
  } = useSchedulerConfig(config);

  // Refs
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const contextMenuRef = useRef(null);
  const dateClickTimer = useRef(null);

  // State
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidePanelOptions, setSidePanelOptions] = useState({
    slotDuration: schedulerConfig.slotDuration,
    slotHeaderInterval: schedulerConfig.slotHeaderInterval,
    timeFormat: schedulerConfig.slotHeaderFormat.hour12 ? '12h' : '24h',
    tooltip: schedulerConfig.tooltip,
  });
  const [eventDialogMenu, setEventDialogMenu] = useState({
    eventData: null,
    mode: null,
    source: null,
    date: null,
    show: false,
  });

  // Hooks
  const customContextMenu = useCustomContextMenu(contextMenuRef);
  const { contextMenu, handleHideCustomContextMenu, handleShowCustomContextMenu } =
    customContextMenu;

  const handleSidePanelOptionChange = (key, value) => {
    setSidePanelOptions((prev) => ({ ...prev, [key]: value }));
  };

  // TODO: Moves these into helper files
  const getNextEventId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}`;
  };

  const handleCalendarEventResize = (eventArg) => {
    const { event } = eventArg;
    try {
      if (onEventResize) {
        onEventResize(toSchedulerEvent(event), {
          source: 'resize',
          previousEvent: toSchedulerEvent(eventArg.oldEvent),
          fullCalendar: eventArg,
        });
      }
    } finally {
      eventArg.revert();
    }
  };

  const handleCalendarEventDrop = (eventArg) => {
    const { event } = eventArg;
    const nextEvent = toSchedulerEvent(event);

    try {
      if (onEventDrop) {
        onEventDrop(nextEvent, {
          source: 'drop',
          previousEvent: toSchedulerEvent(eventArg.oldEvent),
          fullCalendar: eventArg,
        });
      }
    } finally {
      eventArg.revert();
    }
  };

  const handleCloseDialog = () => {
    setEventDialogMenu({
      eventData: null,
      mode: null,
      source: null,
      date: null,
      show: false,
    });
  };

  const handleDeleteEvent = (eventData, source = 'editor') => {
    if (!permissions.deleteEnabled) {
      return;
    }

    if (!eventData || !onEventDelete) return;

    onEventDelete(toSchedulerEvent(eventData), { source });

    handleHideCustomContextMenu();
    handleCloseDialog();
  };

  const handleEventContextMenuOpen = (eventData, domEvent) => {
    const context = {
      type: 'event',
      event: toSchedulerEvent(eventData),
      mouseEvent: domEvent,
    };

    const interceptContextMenu = schedulerConfig.editable && contextMenuConfig.enabled;

    if (interceptContextMenu) {
      domEvent.preventDefault();
    }

    if (interceptContextMenu && contextMenuConfig.onEvents) {
      handleShowCustomContextMenu({
        context,
        menuPos: {
          x: domEvent.x,
          y: domEvent.y,
        },
      });
    }

    if (onEventRightClick) {
      onEventRightClick(context.event, {
        source: 'rightClick',
        nativeEvent: domEvent,
        fullCalendar: eventData,
      });
    }
  };

  const handleEventDialogMenuOpen = (eventData, { mode, source = 'editor', date = null } = {}) => {
    const resolvedMode = mode || (eventData?.id ? 'edit' : 'create');

    if (resolvedMode === 'create' && !permissions.createEnabled) {
      return;
    }

    if (resolvedMode === 'edit' && !permissions.updateEnabled) {
      return;
    }

    const start = eventData?.start || new Date();

    setEventDialogMenu({
      eventData: {
        id: eventData?.id || getNextEventId(),
        title: eventData?.title || '',
        start,
        end: eventData?.end || dateUtils.addHours(start, 1), // Default to 1 hour duration
        description: eventData?.description || '',
      },
      mode: resolvedMode,
      source,
      date,
      show: true,
    });

    handleHideCustomContextMenu();
  };

  // Helpers exposed to custom context menu renderers
  const contextMenuHelpers = {
    close: handleHideCustomContextMenu,
    deleteEvent: (eventData) => handleDeleteEvent(eventData, 'contextMenu'),
    openEditor: (eventData) =>
      handleEventDialogMenuOpen(eventData || null, {
        source: 'contextMenu',
      }),
  };

  const handleEventDialogSave = (newEventData, previousEventData, mode) => {
    const isCreateMode = mode === 'create';
    const canMutate = isCreateMode ? permissions.createEnabled : permissions.updateEnabled;

    if (!canMutate) {
      return;
    }

    const eventId = previousEventData?.id || getNextEventId();

    if (!eventId) return;

    const nextEvent = toSchedulerEvent({
      ...(previousEventData || null),
      id: eventId,
      title: newEventData.title,
      start: newEventData.start,
      end: newEventData.end,
      description: newEventData.description,
    });

    if (mode === 'create') {
      if (onEventCreate) {
        onEventCreate(nextEvent, {
          source: eventDialogMenu.source || 'editor',
          date: eventDialogMenu.date || undefined,
        });
      }
    } else if (mode === 'edit') {
      if (onEventUpdate) {
        onEventUpdate(nextEvent, {
          source: eventDialogMenu.source || 'editor',
          previousEvent: toSchedulerEvent(previousEventData),
        });
      }
    }

    handleCloseDialog();
  };

  const eventEditorHelpers = {
    save: (eventData) =>
      handleEventDialogSave(eventData, eventDialogMenu.eventData, eventDialogMenu.mode),
    cancel: handleCloseDialog,
    delete: (eventData) =>
      handleDeleteEvent(eventData || eventDialogMenu.eventData, eventDialogMenu.source || 'editor'),
  };

  const handleEventDoubleClick = (eventData, domEvent) => {
    if (permissions.updateEnabled) {
      handleEventDialogMenuOpen(eventData, { source: 'doubleClick' });
    }

    if (onEventDoubleClick) {
      onEventDoubleClick(toSchedulerEvent(eventData), {
        source: 'doubleClick',
        nativeEvent: domEvent,
        fullCalendar: eventData,
      });
    }
  };

  const handleEventClick = (eventInfo) => {
    if (!onEventClick) return;

    onEventClick(toSchedulerEvent(eventInfo.event), {
      source: 'click',
      nativeEvent: eventInfo.jsEvent,
      fullCalendar: eventInfo,
    });
  };

  const handleDateClick = (dateInfo) => {
    if (dateClickTimer.current) {
      clearTimeout(dateClickTimer.current);
      dateClickTimer.current = null;

      if (onDateDoubleClick) {
        onDateDoubleClick(
          {
            date: dateInfo.date,
            dateTimestamp: dateUtils.getTemporalPlainDateTime(dateInfo.date),
            dow: dateInfo.dow,
            allDay: dateInfo.allDay ?? false,
          },
          dateInfo.jsEvent,
        );
      }

      return;
    }

    dateClickTimer.current = setTimeout(() => {
      dateClickTimer.current = null;

      if (onDateClick) {
        onDateClick(
          {
            date: dateInfo.date,
            dateTimestamp: dateUtils.getTemporalPlainDateTime(dateInfo.date),
            allDay: dateInfo.allDay,
          },
          {
            source: 'click',
            nativeEvent: dateInfo.jsEvent,
            fullCalendar: dateInfo,
          },
        );
      }
    }, 250);
  };

  const handleSelect = (selectionInfo) => {
    if (!onSelect) return;

    onSelect(
      {
        start: selectionInfo.start,
        startTimestamp: dateUtils.getTemporalPlainDateTime(selectionInfo.start),
        end: selectionInfo.end,
        endTimestamp: dateUtils.getTemporalPlainDateTime(selectionInfo.end),
        allDay: selectionInfo.allDay,
      },
      {
        source: 'select',
        fullCalendar: selectionInfo,
      },
    );
  };

  // Scheduler Content Render Hooks
  const renderDayHeaderContent = (dayHeaderInfo) => {
    if (typeof schedulerConfig.dayHeaderContent === 'function') {
      return schedulerConfig.dayHeaderContent(dayHeaderInfo);
    }

    return <OpDayHeader {...dayHeaderInfo} />;
  };

  const renderEventContent = (eventInfo) => {
    if (typeof eventContent === 'function') {
      return eventContent(eventInfo);
    }

    return undefined;
  };

  // Dependent Hooks
  const { tooltip, handleShowEventTooltip, handleHideEventTooltip } = useEventTooltip();

  const { handleEventDidMount, handleEventWillUnmount } = useEventDidMount({
    handleEventContextMenuOpen,
    handleEventDoubleClick,
    tooltipEnabled: sidePanelOptions.tooltip === 'on',
    handleShowEventTooltip,
    handleHideEventTooltip,
  });

  return (
    <div
      ref={calendarContainerRef}
      className={`op2mise-calendar-container ${isFullscreen ? 'is-fullscreen' : ''} ${schedulerConfig.calendarContainerClass || ''}`.trim()}
    >
      <OpSchedulerToolbar
        header={schedulerConfig.header}
        showAddButton={permissions.createEnabled}
        onAdd={() => handleEventDialogMenuOpen(null)}
        onSettings={() => setIsSettingsOpen((prev) => !prev)}
        isFullscreen={isFullscreen}
        onFullscreen={() => setIsFullscreen((prev) => !prev)}
      />

      <div className="op2mise-calendar-body">
        <OpSchedulerSettingsPanel
          isOpen={isSettingsOpen}
          options={sidePanelOptions}
          onOptionChange={handleSidePanelOptionChange}
          onClose={() => setIsSettingsOpen(false)}
          extraSettingItems={settingsPanelConfig.extraItems}
        />

        {schedulerConfig.isLoading && (
          <div className="op2mise-calendar-loading">
            <OpSchedulerSpinner />
          </div>
        )}

        {!schedulerConfig.isLoading && (
          <FullCalendar
            allDaySlot={schedulerConfig.allDaySlot}
            className="op2mise-calendar"
            dateClick={handleDateClick}
            dayHeaderAlign={schedulerConfig.dayHeaderAlign}
            dayHeaderClass="op2mise-calendar-day-header"
            dayHeaderContent={renderDayHeaderContent}
            dayHeaderInnerClass="op2mise-calendar-day-header-inner"
            dayHeaderRowClass="op2mise-calendar-day-header-row-class"
            dayMinWidth={schedulerConfig.dayMinWidth}
            editable={schedulerConfig.editable}
            eventClick={handleEventClick}
            eventColor="#008662"
            eventContent={typeof eventContent === 'function' ? renderEventContent : undefined}
            eventContrastColor="#ffffff"
            eventDidMount={handleEventDidMount}
            eventDrop={handleCalendarEventDrop}
            eventDurationEditable={permissions.durationEditable}
            // These properly set so that events size according to their time span
            // Without this, events have a minHeight of 15px to accommodate text, which can
            // make events look like they are overlapping
            // (in mil time - event1 end = 00:01; event2 start = 00:02)
            // TODO: We need to properly document this
            eventMinHeight={schedulerConfig.eventMinHeight}
            eventResizableFromStart={schedulerConfig.eventResizableFromStart}
            eventResize={handleCalendarEventResize}
            eventShortHeight={schedulerConfig.eventShortHeight}
            eventStartEditable={permissions.startEditable}
            eventWillUnmount={handleEventWillUnmount}
            events={schedulerConfig.events}
            firstDay={schedulerConfig.firstDay}
            height={schedulerConfig.height}
            initialDate={schedulerConfig.scheduleDateRange.start}
            initialView={schedulerConfig.initialView}
            plugins={[interactionPlugin, scrollGridPlugin, themePlugin, timeGridPlugin]}
            ref={calendarRef}
            schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY}
            scrollTime={schedulerConfig.scrollTime}
            scrollTimeReset={schedulerConfig.scrollTimeReset}
            select={handleSelect}
            selectable={schedulerConfig.selectable}
            slotDuration={sidePanelOptions.slotDuration}
            slotEventOverlap={schedulerConfig.slotEventOverlap}
            slotHeaderFormat={{
              ...schedulerConfig.slotHeaderFormat,
              hour12: sidePanelOptions.timeFormat === '12h',
            }}
            slotHeaderInnerClass="op2mise-calendar-slot-header-inner"
            slotHeaderInterval={sidePanelOptions.slotHeaderInterval}
            slotMaxTime={schedulerConfig.isBroadcastClock ? '30:00:00' : '24:00:00'}
            slotMinHeight={schedulerConfig.slotMinHeight}
            slotMinTime={schedulerConfig.isBroadcastClock ? '06:00:00' : '00:00:00'}
            slotMinWidth={schedulerConfig.slotMinWidth}
            tableClass="op2mise-calendar-table-body"
            timeZone={schedulerConfig.timeZone}
            viewClass="op2mise-calendar-view"
            views={{
              timeGridScrollWeek: {
                type: 'timeGridWeek',
                duration: {
                  days: dateUtils.calculateDurationInDays({
                    startDate: schedulerConfig.scheduleDateRange.start,
                    endDate: schedulerConfig.scheduleDateRange.end,
                  }),
                },
              },
              ...schedulerConfig.views,
            }}
            virtualization
            weekNumberHeaderClass="op2mise-calendar-week-number-header"
          />
        )}

        <OpEventTooltip
          show={tooltip.show}
          event={tooltip.event}
          mousePos={tooltip.mousePos}
          eventContent={typeof eventContent === 'function' ? eventContent : undefined}
        />
      </div>

      {contextMenuConfig.enabled &&
        contextMenu.show &&
        (typeof contextMenuConfig.render === 'function' ? (
          contextMenuConfig.render(contextMenu.context, contextMenuHelpers)
        ) : (
          <OpSchedulerContextMenu
            context={contextMenu.context}
            items={contextMenuConfig.items}
            canUpdate={permissions.updateEnabled}
            canDelete={permissions.deleteEnabled}
            onClose={handleHideCustomContextMenu}
            onDelete={(eventData) => handleDeleteEvent(eventData, 'contextMenu')}
            onEdit={(eventData) =>
              handleEventDialogMenuOpen(eventData, {
                source: 'contextMenu',
              })
            }
            ref={contextMenuRef}
            xPosition={contextMenu.menuPos.x}
            yPosition={contextMenu.menuPos.y}
          />
        ))}

      {eventDialogMenu.show &&
        (typeof eventEditorConfig.render === 'function' ? (
          eventEditorConfig.render(
            {
              mode: eventDialogMenu.mode,
              event: toSchedulerEvent(eventDialogMenu.eventData),
            },
            eventEditorHelpers,
          )
        ) : (
          <OpEventDialog
            eventData={eventDialogMenu.eventData}
            mode={eventDialogMenu.mode}
            canDelete={permissions.deleteEnabled}
            canSave={
              eventDialogMenu.mode === 'create'
                ? permissions.createEnabled
                : permissions.updateEnabled
            }
            onCancel={handleCloseDialog}
            onDelete={(eventData) =>
              handleDeleteEvent(eventData, eventDialogMenu.source || 'editor')
            }
            onSave={handleEventDialogSave}
          />
        ))}
    </div>
  );
}
